import React from 'react';
import { useQuery } from '@apollo/client/react/hooks';
import { useLocation } from '@reach/router';

import { AccountsQueryForRouting } from './accounts';
import { determineCurrentCustomDomain } from './custom_domains';
import { SiteQueryForRouting } from './sites';
import { GroupQueryForRouting } from './groups';
import { AgencyQueryForRouting } from './agency';
import RoutingUtil from '../util/RoutingUtil';

export const getDefaultAccountByCustomDomain = accounts => {
  const customDomain = determineCurrentCustomDomain();
  return customDomain ? accounts.find(r => r.customDomain === customDomain) : null;
}

export const getAccountById = (accountId, accounts = []) => {
  if (accountId && accounts.length) {
    return accounts.find(account => account.id === accountId);
  }
  return null;
};

export const getFirstNonCustomDomainAccount = (accounts = []) => {
  if (!accounts.length) return null;
  return accounts.find(account => !account.customDomain) || accounts[0];
};

/**
 * Determines the default account from available accounts.
 * Priority:
 * 1. Account matching current custom domain
 * 2. First account without custom domain
 * 3. First account
 *
 * @param {Array} accounts - Array of account objects
 * @returns {Object|null} - The default account or null if no accounts
 */
export const getDefaultAccount = (accounts = []) => {
  if (!accounts.length) return null;
  return getDefaultAccountByCustomDomain(accounts) || getFirstNonCustomDomainAccount(accounts);
};

/**
 * Context for route-based data (account/site/group/agency based on current URL)
 */
export const RouteDataContext = React.createContext(null);

/**
 * Hook to access route data from context.
 * Must be used within RouteDataProvider.
 */
export const useRouteData = () => {
  const context = React.useContext(RouteDataContext);
  if (!context) {
    throw new Error('useRouteData must be used within RouteDataProvider');
  }
  return context;
};

/**
 * Provider that fetches route-based data (account/site/group/agency) based on current URL.
 *
 * Fetches:
 * - Account (with optional site or group)
 * - OR Agency
 * - Current user
 * - Accounts the user can access
 *
 * @example
 * <RouteDataProvider>
 *   <YourRoutes />
 * </RouteDataProvider>
 */
export function RouteDataProvider({ children }) {
  const location = useLocation();
  const { pathname } = location;

  const {
    accountId: pathAccountId,
    siteId: pathSiteId,
    groupId: pathGroupId,
    agencyId: pathAgencyId,
  } = RoutingUtil.extractIdsFromPath(pathname);

  const isAgencyRoute = !!pathAgencyId;

  // Query for agency data (if agency route)
  const { loading: agencyLoading, data: agencyData } = useQuery(AgencyQueryForRouting, {
    variables: {
      agencyId: pathAgencyId,
    },
    fetchPolicy: 'cache-first',
    skip: !isAgencyRoute,
  });

  // Query for accounts, user, and membership data (if account route)
  const { loading: accountsLoading, data: accountsData } = useQuery(AccountsQueryForRouting, {
    variables: {
      accountId: pathAccountId,
      siteId: pathSiteId,
      groupId: pathGroupId,
    },
    fetchPolicy: 'cache-first',
    skip: isAgencyRoute,
  });

  const requestedAccountId = React.useMemo(() => {
    return pathAccountId || accountsData?.currentShare?.account?.id;
  }, [pathAccountId, accountsData?.currentShare?.account?.id]);

  // Query for site data (only if siteId is present and not agency route)
  const { loading: siteLoading, data: siteData } = useQuery(SiteQueryForRouting, {
    variables: {
      siteId: pathSiteId,
      accountId: requestedAccountId,
    },
    fetchPolicy: 'cache-first',
    skip: !pathSiteId || !requestedAccountId || isAgencyRoute,
  });

  // Query for group data (only if groupId is present and not agency route)
  const { loading: groupLoading, data: groupData } = useQuery(GroupQueryForRouting, {
    variables: {
      groupId: pathGroupId,
      accountId: requestedAccountId,
    },
    fetchPolicy: 'cache-first',
    skip: !pathGroupId || !requestedAccountId || isAgencyRoute,
  });

  const loading = isAgencyRoute
    ? agencyLoading
    : (accountsLoading || siteLoading || groupLoading);

  const accounts = React.useMemo(() => {
    return isAgencyRoute
      ? (agencyData?.accounts?.nodes || [])
      : (accountsData?.accounts?.nodes || []);
  }, [isAgencyRoute, agencyData?.accounts?.nodes, accountsData?.accounts?.nodes]);

  const currentUser = React.useMemo(() => {
    return isAgencyRoute ? agencyData?.currentUser : (accountsData?.currentUser || {});
  }, [isAgencyRoute, agencyData?.currentUser, accountsData?.currentUser]);

  const account = React.useMemo(() => {
    if (!accounts.length) return null;

    // Only return a default account if there's an accountId in the path
    // Otherwise, return null (e.g., for super users selecting an account)
    if (!pathAccountId) {
      return null;
    }

    return (
      getAccountById(requestedAccountId, accounts) ||
      getDefaultAccountByCustomDomain(accounts) ||
      getFirstNonCustomDomainAccount(accounts)
    );
  }, [pathAccountId, requestedAccountId, accounts]);

  const agency = React.useMemo(() => {
    return isAgencyRoute ? agencyData?.agency : (account?.agency || null);
  }, [isAgencyRoute, account?.agency, agencyData?.agency]);

  const customDomain = React.useMemo(() => {
    return isAgencyRoute ? agency?.customDomain : account?.customDomain || null;
  }, [isAgencyRoute, account?.customDomain, agency?.customDomain]);

  const accountId = account?.id || requestedAccountId || null;
  const agencyId = agency?.id || pathAgencyId || null;
  const site = siteData?.site || null;
  const group = groupData?.group || null;
  const siteId = site?.id || pathSiteId || null;
  const groupId = group?.id || pathGroupId || null;

  const value = React.useMemo(() => ({
    loading,
    // Route-based entities
    accountId,
    account,
    site,
    siteId,
    group,
    groupId,
    agency,
    agencyId,
    customDomain,
    usesSsoId: customDomain?.usesSsoId || false,

    // User and accounts
    currentUser,
    accounts,

    // Raw query data for advanced use cases
    accountsData: !isAgencyRoute ? accountsData : null,
    siteData: !isAgencyRoute ? siteData : null,
    groupData: !isAgencyRoute ? groupData : null,
    agencyData: isAgencyRoute ? agencyData : null,

    // Route info
    pathAccountId,
    pathSiteId,
    pathGroupId,
    pathAgencyId,
    isAgencyRoute,

    // Helper function to get prefixed path based on current route
    getBaseUrl() {
      if (isAgencyRoute) return RoutingUtil.getAgencyPrefixedPath("", agencyId);

      let path = "";
      if (siteId) {
        path = `/sites/${siteId}`;
      } else if (groupId) {
        path = `/groups/${groupId}`;
      } else if (accountId) {
        path = `/dashboards`;
      }
      return path !== "" ? RoutingUtil.getAccountPrefixedPath(path, accountId) : "/";
    },
  }), [
    loading,
    isAgencyRoute,
    accountId,
    account,
    site,
    siteId,
    group,
    groupId,
    agency,
    agencyId,
    customDomain,
    currentUser,
    accounts,
    accountsData,
    agencyData,
    siteData,
    groupData,
    pathAccountId,
    pathSiteId,
    pathGroupId,
    pathAgencyId,
  ]);

  return (
    <RouteDataContext.Provider value={value}>
      {children}
    </RouteDataContext.Provider>
  );
}
